import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Users, Briefcase, Search, Trash2, Eye, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Shield, Mail, Phone, Globe, Calendar, Loader2, Download,
  AlertTriangle, RotateCcw, Archive, Trophy, FileText, CalendarDays, Target, Activity,
  Zap, Mic
} from 'lucide-react';
import { ConvergeSpeakersTab } from './ConvergeSpeakersTab';
import { SiteContactTab } from './SiteContactTab';

const API = process.env.REACT_APP_BACKEND_URL;

const SuperAdminPanel = ({ token }) => {
  const [activeTab, setActiveTab] = useState('activity');
  
  const tabs = [
    { id: 'activity', label: 'Activity Feed', icon: Activity },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'jobs', label: 'Job Approvals', icon: Briefcase },
    { id: 'hiring-challenges', label: 'Hiring Challenges', icon: Trophy },
    { id: 'enterprise-challenges', label: 'Enterprise Challenges', icon: Target },
    { id: 'assessments', label: 'Assessments', icon: FileText },
    { id: 'events', label: 'Events', icon: CalendarDays },
    { id: 'verified-talent', label: 'Verified Talent', icon: Shield },
    { id: 'llm-health', label: 'LLM Health', icon: Zap },
    { id: 'converge-speakers', label: 'CONVERGE', icon: Mic },
    { id: 'site-contact', label: 'Contact & WhatsApp', icon: Phone },
  ];

  return (
    <div data-testid="super-admin-panel">
      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`super-tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'activity' && <ActivityFeedTab token={token} />}
      {activeTab === 'companies' && <CompaniesTab token={token} />}
      {activeTab === 'students' && <StudentsTab token={token} />}
      {activeTab === 'jobs' && <JobsTab token={token} />}
      {activeTab === 'hiring-challenges' && (
        <ModerationTab
          token={token}
          resource="hiring-challenges"
          resourceLabel="Hiring Challenges"
          itemsKey="challenges"
          titleField="title"
          subtitleField="company_name"
          detailExtraKeys={['participants_count']}
        />
      )}
      {activeTab === 'enterprise-challenges' && (
        <ModerationTab
          token={token}
          resource="enterprise-challenges"
          resourceLabel="Enterprise Challenges"
          itemsKey="challenges"
          titleField="title"
          subtitleField="company_name"
          detailExtraKeys={['registrations_count']}
        />
      )}
      {activeTab === 'assessments' && (
        <ModerationTab
          token={token}
          resource="assessments"
          resourceLabel="Assessments"
          itemsKey="assessments"
          titleField="title"
          subtitleField="company_name"
          extraColumn={{ label: 'Candidates', field: 'candidates_count' }}
        />
      )}
      {activeTab === 'events' && (
        <ModerationTab
          token={token}
          resource="events"
          resourceLabel="Events"
          itemsKey="events"
          titleField="title"
          subtitleField="company_name"
          statusFieldInDoc="status"
          detailExtraKeys={['registrations_count']}
        />
      )}
      {activeTab === 'verified-talent' && <VerifiedTalentAdminTab token={token} />}
      {activeTab === 'llm-health' && <LLMHealthTab token={token} />}
      {activeTab === 'converge-speakers' && <ConvergeSpeakersTab token={token} />}
      {activeTab === 'site-contact' && <SiteContactTab token={token} />}
    </div>
  );
};

// Companies Management Tab
const CompaniesTab = ({ token }) => {
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/super/companies?page=${page}&limit=15&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
    } catch (err) { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCompanies(); }, [page, search]);

  const deleteCompany = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await axios.delete(`${API}/api/admin/super/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Company deleted');
      fetchCompanies();
      if (selectedCompany?.id === id) setSelectedCompany(null);
    } catch { toast.error('Failed to delete'); }
  };

  const viewCompanyDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/api/admin/super/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedCompany(res.data);
    } catch { toast.error('Failed to load details'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Companies ({total})</h3>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search companies..." data-testid="search-companies" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={24} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Joined</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(c => (
                    <tr key={c.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedCompany?.id === c.id ? 'bg-blue-50' : ''}`}
                      onClick={() => viewCompanyDetail(c.id)} data-testid={`company-row-${c.id}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{c.company_name || c.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{c.email}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{c.created_at?.slice(0, 10) || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); deleteCompany(c.id); }}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No companies found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
              <span>Page {page} of {Math.ceil(total / 15) || 1}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"><ChevronLeft size={14} /></button>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 15)}
                  className="px-3 py-1 border rounded disabled:opacity-50"><ChevronRight size={14} /></button>
              </div>
            </div>
          </div>

          {/* Detail Panel */}
          <div>
            {selectedCompany ? (
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-4">
                <h4 className="font-bold text-gray-900 mb-3">{selectedCompany.company_name || selectedCompany.name}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600"><Mail size={14} />{selectedCompany.email}</div>
                  {selectedCompany.phone && <div className="flex items-center gap-2 text-gray-600"><Phone size={14} />{selectedCompany.phone}</div>}
                  {selectedCompany.website && <div className="flex items-center gap-2 text-gray-600"><Globe size={14} />{selectedCompany.website}</div>}
                  {selectedCompany.industry && <div className="flex items-center gap-2 text-gray-600"><Building2 size={14} />{selectedCompany.industry}</div>}
                  <div className="flex items-center gap-2 text-gray-600"><Calendar size={14} />Joined: {selectedCompany.created_at?.slice(0, 10)}</div>
                </div>
                {selectedCompany.stats && (
                  <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t">
                    <div className="text-center p-2 bg-gray-50 rounded"><p className="text-lg font-bold">{selectedCompany.stats.jobs}</p><p className="text-[10px] text-gray-500">Jobs</p></div>
                    <div className="text-center p-2 bg-gray-50 rounded"><p className="text-lg font-bold">{selectedCompany.stats.events}</p><p className="text-[10px] text-gray-500">Events</p></div>
                    <div className="text-center p-2 bg-gray-50 rounded"><p className="text-lg font-bold">{selectedCompany.stats.challenges}</p><p className="text-[10px] text-gray-500">Challenges</p></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400 text-sm">
                Select a company to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Students Management Tab
const StudentsTab = ({ token }) => {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/super/students?page=${page}&limit=15&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(res.data.students || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load students'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStudents(); }, [page, search]);

  const deleteStudent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    try {
      await axios.delete(`${API}/api/admin/super/students/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Student deleted');
      fetchStudents();
      if (selectedStudent?.id === id) setSelectedStudent(null);
    } catch { toast.error('Failed to delete'); }
  };

  const viewStudentDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/api/admin/super/students/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedStudent(res.data);
    } catch { toast.error('Failed to load details'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Students ({total})</h3>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search students..." data-testid="search-students" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={24} /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Email</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">College</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedStudent?.id === s.id ? 'bg-blue-50' : ''}`}
                      onClick={() => viewStudentDetail(s.id)} data-testid={`student-row-${s.id}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{s.full_name || s.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500">{s.email}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{s.college || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); deleteStudent(s.id); }}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {students.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No students found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
              <span>Page {page} of {Math.ceil(total / 15) || 1}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-50"><ChevronLeft size={14} /></button>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 15)}
                  className="px-3 py-1 border rounded disabled:opacity-50"><ChevronRight size={14} /></button>
              </div>
            </div>
          </div>

          <div>
            {selectedStudent ? (
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-4">
                <h4 className="font-bold text-gray-900 mb-3">{selectedStudent.full_name || selectedStudent.name}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600"><Mail size={14} />{selectedStudent.email}</div>
                  {selectedStudent.phone && <div className="flex items-center gap-2 text-gray-600"><Phone size={14} />{selectedStudent.phone}</div>}
                  {selectedStudent.college && <div className="flex items-center gap-2 text-gray-600"><Building2 size={14} />{selectedStudent.college}</div>}
                  {selectedStudent.username && <div className="flex items-center gap-2 text-gray-600"><Users size={14} />@{selectedStudent.username}</div>}
                  <div className="flex items-center gap-2 text-gray-600"><Calendar size={14} />Joined: {selectedStudent.created_at?.slice(0, 10)}</div>
                </div>
                {selectedStudent.stats && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold">{selectedStudent.stats.applications}</p>
                      <p className="text-[10px] text-gray-500">Applications</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-400 text-sm">
                Select a student to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Jobs Approval Tab
const JobsTab = ({ token }) => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/super/jobs?page=${page}&limit=15&status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load jobs'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchJobs(); }, [page, statusFilter]);

  const approveJob = async (id) => {
    try {
      await axios.put(`${API}/api/admin/super/jobs/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job approved');
      fetchJobs();
    } catch { toast.error('Failed to approve'); }
  };

  const rejectJob = async (id) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    try {
      await axios.put(`${API}/api/admin/super/jobs/${id}/reject`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job rejected');
      fetchJobs();
    } catch { toast.error('Failed to reject'); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Soft-delete this job? It can be restored later from the "Deleted" tab.')) return;
    try {
      await axios.delete(`${API}/api/admin/super/jobs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job deleted (can be restored)');
      fetchJobs();
    } catch { toast.error('Failed to delete'); }
  };

  const restoreJob = async (id) => {
    try {
      const res = await axios.put(`${API}/api/admin/super/jobs/${id}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data?.message || 'Job restored');
      fetchJobs();
    } catch { toast.error('Failed to restore'); }
  };

  const purgeJob = async (id) => {
    if (!window.confirm('Permanently purge this job? This CANNOT be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/super/jobs/${id}/purge`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Job permanently purged');
      fetchJobs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to purge'); }
  };

  const getStatusBadge = (job) => {
    if (job.is_deleted || job.approval_status === 'deleted') {
      return <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">Deleted</span>;
    }
    const s = job.approval_status || (job.is_active ? 'approved' : 'pending');
    if (s === 'approved') return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Approved</span>;
    if (s === 'rejected') return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Rejected</span>;
    return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Pending</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Job Posts ({total})</h3>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'rejected', 'deleted'].map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`} data-testid={`job-filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={24} /></div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Job Title</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Posted</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const isDeleted = job.is_deleted || job.approval_status === 'deleted';
                  const currentStatus = isDeleted ? 'deleted' : (job.approval_status || (job.is_active ? 'approved' : 'pending'));
                  return (
                  <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`job-row-${job.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{job.title}</td>
                    <td className="px-4 py-3 text-gray-500">{job.company_name || '-'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{job.job_type || '-'}</td>
                    <td className="px-4 py-3">{getStatusBadge(job)}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{job.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isDeleted ? (
                          <>
                            <button onClick={() => restoreJob(job.id)}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Restore"
                              data-testid={`restore-job-${job.id}`}>
                              <RotateCcw size={16} />
                            </button>
                            <button onClick={() => purgeJob(job.id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Purge Permanently"
                              data-testid={`purge-job-${job.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            {currentStatus !== 'approved' && (
                              <button onClick={() => approveJob(job.id)}
                                className="p-1.5 text-green-500 hover:bg-green-50 rounded" title="Approve"
                                data-testid={`approve-job-${job.id}`}>
                                <CheckCircle size={16} />
                              </button>
                            )}
                            {currentStatus !== 'rejected' && (
                              <button onClick={() => rejectJob(job.id)}
                                className="p-1.5 text-orange-500 hover:bg-orange-50 rounded" title="Reject"
                                data-testid={`reject-job-${job.id}`}>
                                <XCircle size={16} />
                              </button>
                            )}
                            <button onClick={() => deleteJob(job.id)}
                              className="p-1.5 text-red-400 hover:bg-red-50 rounded" title="Delete (soft)"
                              data-testid={`delete-job-${job.id}`}>
                              <Archive size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {jobs.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No jobs found</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
            <span>Page {page} of {Math.ceil(total / 15) || 1}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"><ChevronLeft size={14} /></button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 15)}
                className="px-3 py-1 border rounded disabled:opacity-50"><ChevronRight size={14} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminPanel;

// ==================== Activity Feed Tab ====================
// Unified timeline across jobs / hiring-challenges / enterprise-challenges / assessments / events.
const ActivityFeedTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [adminFilter, setAdminFilter] = useState('');

  const ENTITY_TYPES = [
    { value: 'all', label: 'All Entities' },
    { value: 'job', label: 'Jobs' },
    { value: 'hiring_challenge', label: 'Hiring Challenges' },
    { value: 'enterprise_challenge', label: 'Enterprise Challenges' },
    { value: 'assessment', label: 'Assessments' },
    { value: 'event', label: 'Events' },
  ];

  const ACTIONS = [
    { value: 'all', label: 'All Actions' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'deleted', label: 'Deleted' },
    { value: 'restored', label: 'Restored' },
  ];

  const ACTION_META = {
    approved: { color: 'bg-green-100 text-green-700', border: 'border-green-200', Icon: CheckCircle },
    rejected: { color: 'bg-red-100 text-red-700', border: 'border-red-200', Icon: XCircle },
    deleted:  { color: 'bg-gray-100 text-gray-700', border: 'border-gray-200', Icon: Archive },
    restored: { color: 'bg-blue-100 text-blue-700', border: 'border-blue-200', Icon: RotateCcw },
  };

  const ENTITY_META = {
    job: { Icon: Briefcase, label: 'Job' },
    hiring_challenge: { Icon: Trophy, label: 'Hiring Challenge' },
    enterprise_challenge: { Icon: Target, label: 'Enterprise Challenge' },
    assessment: { Icon: FileText, label: 'Assessment' },
    event: { Icon: CalendarDays, label: 'Event' },
  };

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (adminFilter.trim()) params.append('admin_username', adminFilter.trim());
      const res = await axios.get(`${API}/api/admin/super/activity-feed?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data.events || []);
    } catch {
      toast.error('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeed(); /* eslint-disable-next-line */ }, [entityFilter, actionFilter]);

  const formatRelative = (ts) => {
    if (!ts) return '';
    try {
      const then = new Date(ts);
      const diffMs = Date.now() - then.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      return then.toLocaleDateString();
    } catch { return ts; }
  };

  // Group events by date (YYYY-MM-DD)
  const grouped = events.reduce((acc, e) => {
    const day = (e.timestamp || '').slice(0, 10) || 'unknown';
    if (!acc[day]) acc[day] = [];
    acc[day].push(e);
    return acc;
  }, {});
  const sortedDays = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div data-testid="activity-feed-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <h3 className="text-lg font-bold text-gray-900 mr-auto">
          Activity Feed <span className="text-gray-400 font-normal">({events.length})</span>
        </h3>
        <select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          data-testid="activity-entity-filter"
        >
          {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
          data-testid="activity-action-filter"
        >
          {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={adminFilter}
            onChange={(e) => setAdminFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchFeed()}
            placeholder="Filter by admin…"
            className="pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm w-44"
            data-testid="activity-admin-filter"
          />
        </div>
        <button
          onClick={fetchFeed}
          className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
          data-testid="activity-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
          <Activity className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-500 text-sm">No activity matching the current filters.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDays.map(day => {
            const dayLabel = (() => {
              const today = new Date().toISOString().slice(0, 10);
              const yesterday = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
              if (day === today) return 'Today';
              if (day === yesterday) return 'Yesterday';
              try {
                return new Date(day).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
              } catch { return day; }
            })();
            return (
              <div key={day}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-shrink-0 px-3 py-1 bg-gray-100 rounded-full text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {dayLabel}
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">{grouped[day].length} events</span>
                </div>
                <div className="space-y-2">
                  {grouped[day].map((e, idx) => {
                    const meta = ACTION_META[e.action] || ACTION_META.approved;
                    const ent = ENTITY_META[e.entity_type] || { Icon: FileText, label: e.entity_type };
                    return (
                      <div
                        key={`${e.entity_id}-${e.action}-${e.timestamp}-${idx}`}
                        className={`bg-white border ${meta.border} rounded-lg p-4 flex items-start gap-3 hover:shadow-sm transition-shadow`}
                        data-testid={`activity-event-${e.entity_id}-${e.action}`}
                      >
                        <div className={`p-2 rounded-lg ${meta.color} flex-shrink-0`}>
                          <meta.Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${meta.color}`}>
                              {e.action.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <ent.Icon size={11} /> {ent.label}
                            </span>
                            <span className="ml-auto text-xs text-gray-400">{formatRelative(e.timestamp)}</span>
                          </div>
                          <p className="font-medium text-gray-900 truncate" title={e.title}>
                            {e.title}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                            {e.company_name && (
                              <span className="flex items-center gap-1">
                                <Building2 size={10} />
                                {e.company_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Shield size={10} />
                              {e.actor}
                            </span>
                            <span className="font-mono text-gray-300">{e.entity_id}</span>
                          </div>
                          {e.reason && (
                            <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 px-2 py-1 rounded">
                              <strong>Reason:</strong> {e.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== Generic Moderation Tab ====================
// Reusable component for Hiring Challenges, Enterprise Challenges, Assessments, Events.
// Supports: list (with status filter), view detail, approve, reject, soft-delete, restore, purge.
const ModerationTab = ({
  token,
  resource,            // e.g. 'hiring-challenges', 'assessments', 'events'
  resourceLabel,       // e.g. 'Hiring Challenges'
  itemsKey,            // response key holding the array, e.g. 'challenges'
  titleField = 'title',
  subtitleField = 'company_name',
  statusFieldInDoc = 'approval_status',  // 'status' for events
  extraColumn = null,  // optional { label, field }
  detailExtraKeys = [], // optional numeric fields from detail response to surface
}) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/super/${resource}?page=${page}&limit=15&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setItems(res.data[itemsKey] || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error(`Failed to load ${resourceLabel.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [page, statusFilter]);

  const viewDetail = async (id) => {
    setDetailLoading(true);
    setSelected(id);
    setDetailData(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/super/${resource}/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailData(res.data);
    } catch {
      toast.error('Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const doAction = async (method, path, body = undefined, successMsg) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (method === 'DELETE') {
        await axios.delete(`${API}/api/admin/super/${resource}/${path}`, config);
      } else if (method === 'PUT') {
        await axios.put(`${API}/api/admin/super/${resource}/${path}`, body || {}, config);
      }
      toast.success(successMsg);
      fetchItems();
      if (selected === path.split('/')[0]) setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const approve = (id) => doAction('PUT', `${id}/approve`, {}, 'Approved');
  const reject = (id) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    doAction('PUT', `${id}/reject`, { reason }, 'Rejected');
  };
  const softDelete = (id) => {
    if (!window.confirm('Soft-delete this? It can be restored later from the "Deleted" tab.')) return;
    doAction('DELETE', `${id}`, null, 'Deleted (can be restored)');
  };
  const restore = (id) => doAction('PUT', `${id}/restore`, {}, 'Restored');
  const purge = (id) => {
    if (!window.confirm('Permanently purge? This CANNOT be undone.')) return;
    doAction('DELETE', `${id}/purge`, null, 'Permanently purged');
  };

  const getStatusBadge = (item) => {
    const isDeleted = item.is_deleted || item[statusFieldInDoc] === 'deleted';
    if (isDeleted) return <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-medium">Deleted</span>;
    const s = item[statusFieldInDoc] || (item.is_active ? 'approved' : 'pending');
    if (['approved', 'live', 'active'].includes(s)) return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Approved</span>;
    if (s === 'rejected') return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Rejected</span>;
    if (s === 'draft') return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">Draft</span>;
    return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Pending</span>;
  };

  return (
    <div data-testid={`moderation-tab-${resource}`}>
      <div className={`grid ${selected ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
        {/* List */}
        <div className={selected ? 'lg:col-span-2' : ''}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{resourceLabel} ({total})</h3>
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'approved', 'rejected', 'deleted'].map(s => (
                <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`} data-testid={`${resource}-filter-${s}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={24} /></div>
          ) : (
            <>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Title</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                      {extraColumn && <th className="px-4 py-3 text-left font-semibold text-gray-600">{extraColumn.label}</th>}
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Created</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => {
                      const isDeleted = item.is_deleted || item[statusFieldInDoc] === 'deleted';
                      const currentStatus = isDeleted ? 'deleted' : (item[statusFieldInDoc] || 'pending');
                      return (
                      <tr key={item.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selected === item.id ? 'bg-blue-50' : ''}`}
                          data-testid={`${resource}-row-${item.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900 truncate max-w-[260px]">{item[titleField] || '-'}</td>
                        <td className="px-4 py-3 text-gray-500">{item[subtitleField] || '-'}</td>
                        {extraColumn && <td className="px-4 py-3 text-gray-500">{item[extraColumn.field] ?? 0}</td>}
                        <td className="px-4 py-3">{getStatusBadge(item)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{(item.created_at || '').slice(0, 10)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => viewDetail(item.id)}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="View Details"
                              data-testid={`${resource}-view-${item.id}`}>
                              <Eye size={16} />
                            </button>
                            {isDeleted ? (
                              <>
                                <button onClick={() => restore(item.id)}
                                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Restore"
                                  data-testid={`${resource}-restore-${item.id}`}>
                                  <RotateCcw size={16} />
                                </button>
                                <button onClick={() => purge(item.id)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Purge Permanently"
                                  data-testid={`${resource}-purge-${item.id}`}>
                                  <Trash2 size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                {!['approved', 'live', 'active'].includes(currentStatus) && (
                                  <button onClick={() => approve(item.id)}
                                    className="p-1.5 text-green-500 hover:bg-green-50 rounded" title="Approve"
                                    data-testid={`${resource}-approve-${item.id}`}>
                                    <CheckCircle size={16} />
                                  </button>
                                )}
                                {currentStatus !== 'rejected' && (
                                  <button onClick={() => reject(item.id)}
                                    className="p-1.5 text-orange-500 hover:bg-orange-50 rounded" title="Reject"
                                    data-testid={`${resource}-reject-${item.id}`}>
                                    <XCircle size={16} />
                                  </button>
                                )}
                                <button onClick={() => softDelete(item.id)}
                                  className="p-1.5 text-red-400 hover:bg-red-50 rounded" title="Delete (soft)"
                                  data-testid={`${resource}-delete-${item.id}`}>
                                  <Archive size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                    {items.length === 0 && (
                      <tr><td colSpan={extraColumn ? 6 : 5} className="px-4 py-8 text-center text-gray-400">No records found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
                <span>Page {page} of {Math.ceil(total / 15) || 1}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"><ChevronLeft size={14} /></button>
                  <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 15)}
                    className="px-3 py-1 border rounded disabled:opacity-50"><ChevronRight size={14} /></button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-4" data-testid={`${resource}-detail-panel`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-gray-900">Details</h4>
                <button onClick={() => { setSelected(null); setDetailData(null); }}
                  className="text-gray-400 hover:text-gray-600">
                  <XCircle size={18} />
                </button>
              </div>
              {detailLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" size={20} /></div>
              ) : detailData ? (
                <ModerationDetailView
                  data={detailData}
                  detailExtraKeys={detailExtraKeys}
                  statusFieldInDoc={statusFieldInDoc}
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ModerationDetailView = ({ data, detailExtraKeys = [], statusFieldInDoc }) => {
  // Find primary doc (challenge | assessment | event)
  const doc = data.challenge || data.assessment || data.event || {};
  const company = data.company;
  const docFields = Object.entries(doc).filter(([k, v]) =>
    typeof v !== 'object' && v !== null && v !== undefined && v !== '' &&
    !['_id', 'id'].includes(k) &&
    k.length < 40
  ).slice(0, 18);

  return (
    <div className="space-y-4 text-sm max-h-[70vh] overflow-y-auto pr-1">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Title</p>
        <p className="font-semibold text-gray-900">{doc.title || doc.name || doc.id}</p>
      </div>
      {doc.description && (
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Description</p>
          <p className="text-gray-700 text-sm whitespace-pre-wrap">{String(doc.description).slice(0, 400)}{doc.description?.length > 400 ? '…' : ''}</p>
        </div>
      )}

      {detailExtraKeys.map(k => (
        data[k] !== undefined && (
          <div key={k} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <span className="text-xs text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</span>
            <span className="font-bold text-gray-900">{data[k]}</span>
          </div>
        )
      ))}

      {company && (
        <div className="p-3 bg-purple-50 border border-purple-100 rounded">
          <p className="text-xs text-purple-700 uppercase tracking-wide font-semibold mb-2">Company</p>
          <p className="font-semibold text-gray-900">{company.company_name}</p>
          {company.email && <p className="text-xs text-gray-600">{company.email}</p>}
          {company.industry && <p className="text-xs text-gray-500">{company.industry}</p>}
        </div>
      )}

      {data.stats && (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data.stats).map(([k, v]) => (
            <div key={k} className="p-2 bg-blue-50 rounded text-center">
              <p className="text-xs text-blue-600 uppercase tracking-wide">{k.replace(/_/g, ' ')}</p>
              <p className="font-bold text-gray-900">{v}</p>
            </div>
          ))}
        </div>
      )}

      <details className="group">
        <summary className="cursor-pointer text-xs text-gray-500 uppercase tracking-wide font-semibold hover:text-gray-700">
          All Fields ({docFields.length})
        </summary>
        <div className="mt-2 space-y-1.5">
          {docFields.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 py-1 border-b border-gray-100 text-xs">
              <span className="text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</span>
              <span className="text-gray-900 font-medium text-right truncate max-w-[180px]" title={String(v)}>
                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v).slice(0, 80)}
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};


// ==================== Verified Talent Admin Tab ====================
const VerifiedTalentAdminTab = ({ token }) => {
  const [plans, setPlans] = useState([]);
  const [config, setConfig] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [subtab, setSubtab] = useState('analytics');
  const [profileFilter, setProfileFilter] = useState('all');
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, c, pr, a] = await Promise.all([
        axios.get(`${API}/api/admin/verified-talent/plans`, { headers }),
        axios.get(`${API}/api/admin/verified-talent/config`, { headers }),
        axios.get(`${API}/api/admin/verified-talent/profiles?status=${profileFilter}&limit=100`, { headers }),
        axios.get(`${API}/api/admin/verified-talent/analytics`, { headers }),
      ]);
      setPlans(p.data.plans || []);
      setConfig(c.data);
      setProfiles(pr.data.profiles || []);
      setAnalytics(a.data);
    } catch {
      toast.error('Failed to load verified-talent admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [profileFilter]);

  const savePlan = async (plan) => {
    try {
      if (plan.__new) {
        const { __new, id, ...rest } = plan;
        await axios.post(`${API}/api/admin/verified-talent/plans`, rest, { headers });
      } else {
        const { id, _id, ...rest } = plan;
        await axios.put(`${API}/api/admin/verified-talent/plans/${plan.id}`, rest, { headers });
      }
      toast.success('Plan saved');
      setEditingPlan(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this plan? Students with existing purchases will keep their access.')) return;
    try {
      await axios.delete(`${API}/api/admin/verified-talent/plans/${id}`, { headers });
      toast.success('Plan deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const saveConfig = async (updated) => {
    try {
      await axios.put(`${API}/api/admin/verified-talent/config`, updated, { headers });
      toast.success('Config saved');
      setConfig(updated);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const addAttempts = async (studentId) => {
    const count = parseInt(window.prompt('How many bonus attempts to add?', '1'), 10);
    if (!count || count < 1) return;
    try {
      await axios.post(`${API}/api/admin/verified-talent/profiles/${studentId}/add-attempts`, { count }, { headers });
      toast.success(`Added ${count} attempt(s)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const manualApprove = async (studentId) => {
    if (!window.confirm('Manually activate the Verified badge for this student?')) return;
    try {
      await axios.post(`${API}/api/admin/verified-talent/profiles/${studentId}/manual-approve`, {}, { headers });
      toast.success('Badge activated');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revoke = async (studentId) => {
    if (!window.confirm('Revoke this student\'s Verified badge?')) return;
    try {
      await axios.post(`${API}/api/admin/verified-talent/profiles/${studentId}/revoke`, {}, { headers });
      toast.success('Badge revoked');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={28} /></div>;
  }

  const subtabs = [
    { id: 'analytics', label: 'Analytics' },
    { id: 'plans',     label: 'Plans' },
    { id: 'config',    label: 'Config' },
    { id: 'profiles',  label: 'Verified Profiles' },
  ];

  return (
    <div data-testid="verified-talent-admin-tab">
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        {subtabs.map(s => (
          <button
            key={s.id}
            onClick={() => setSubtab(s.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              subtab === s.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`vt-subtab-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Analytics */}
      {subtab === 'analytics' && analytics && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Active Badges', value: analytics.active_badges, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
              { label: 'Revenue', value: `₹${analytics.revenue}`, color: 'text-purple-700 bg-purple-50 border-purple-200' },
              { label: 'Paying Students', value: analytics.paying_students, color: 'text-blue-700 bg-blue-50 border-blue-200' },
              { label: 'Pass Rate', value: `${analytics.pass_rate}%`, color: 'text-amber-700 bg-amber-50 border-amber-200' },
            ].map(s => (
              <div key={s.label} className={`p-4 rounded-lg border ${s.color}`}>
                <p className="text-xs uppercase tracking-wide opacity-70">{s.label}</p>
                <p className="text-2xl font-bold mt-1">{s.value}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-4 bg-gray-50 rounded border">
              <p className="text-xs text-gray-500 uppercase">Total Profiles</p>
              <p className="text-xl font-bold text-gray-900">{analytics.total_profiles}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded border">
              <p className="text-xs text-gray-500 uppercase">Total Attempts</p>
              <p className="text-xl font-bold text-gray-900">{analytics.total_attempts}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded border">
              <p className="text-xs text-gray-500 uppercase">Passed</p>
              <p className="text-xl font-bold text-emerald-700">{analytics.passed}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded border">
              <p className="text-xs text-gray-500 uppercase">Failed</p>
              <p className="text-xl font-bold text-red-600">{analytics.failed}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plans */}
      {subtab === 'plans' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900">Plans ({plans.length})</h3>
            <button
              onClick={() => setEditingPlan({ __new: true, id: 'new', code: '', name: '', price: 0, currency: 'INR', attempts: 1, validity_months: 6, is_recommended: false, is_active: true, features: [''], sort_order: plans.length + 1 })}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded text-sm font-semibold"
              data-testid="vt-add-plan-btn"
            >+ Add Plan</button>
          </div>
          <div className="space-y-3">
            {plans.map(p => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4" data-testid={`vt-plan-${p.code}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900">{p.name}</span>
                    {p.is_recommended && <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-700 rounded">RECOMMENDED</span>}
                    {!p.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-200 text-gray-600 rounded">INACTIVE</span>}
                  </div>
                  <p className="text-xs text-gray-500">₹{p.price} · {p.attempts} attempt{p.attempts > 1 ? 's' : ''} · {p.validity_months} months · {(p.features || []).length} features</p>
                </div>
                <button onClick={() => setEditingPlan({ ...p })} className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded">Edit</button>
                <button onClick={() => deletePlan(p.id)} className="px-3 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded">Delete</button>
              </div>
            ))}
          </div>

          {editingPlan && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditingPlan(null)}>
              <div className="bg-white max-w-lg w-full rounded-lg p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <h4 className="font-bold text-gray-900 mb-3">{editingPlan.__new ? 'New Plan' : `Edit ${editingPlan.name}`}</h4>
                {[
                  ['code', 'Code (e.g. pro)', 'text'],
                  ['name', 'Display Name', 'text'],
                  ['price', 'Price (₹)', 'number'],
                  ['attempts', 'AI Interview Attempts', 'number'],
                  ['validity_months', 'Badge Validity (months)', 'number'],
                  ['sort_order', 'Sort Order', 'number'],
                ].map(([k, label, type]) => (
                  <div key={k} className="mb-3">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                    <input
                      type={type}
                      value={editingPlan[k] ?? ''}
                      onChange={e => setEditingPlan({ ...editingPlan, [k]: type === 'number' ? Number(e.target.value) : e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                ))}
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!editingPlan.is_recommended}
                      onChange={e => setEditingPlan({ ...editingPlan, is_recommended: e.target.checked })} />
                    Recommended
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={editingPlan.is_active !== false}
                      onChange={e => setEditingPlan({ ...editingPlan, is_active: e.target.checked })} />
                    Active
                  </label>
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Features (one per line)</label>
                  <textarea
                    value={(editingPlan.features || []).join('\n')}
                    onChange={e => setEditingPlan({ ...editingPlan, features: e.target.value.split('\n').filter(x => x.trim()) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm h-28 font-mono"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingPlan(null)} className="px-4 py-2 text-sm bg-gray-100 rounded">Cancel</button>
                  <button onClick={() => savePlan(editingPlan)} className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded">Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Config */}
      {subtab === 'config' && config && (
        <div className="max-w-xl space-y-4 bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="font-bold text-gray-900">Global Config</h3>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Approval Threshold (%)</label>
            <input type="number" min="0" max="100" value={config.approval_threshold}
              onChange={e => setConfig({ ...config, approval_threshold: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
            <p className="text-xs text-gray-500 mt-1">Minimum score (0-100) a candidate must achieve to be auto-verified.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Cooldown Between Attempts (minutes)</label>
            <input type="number" min="0" value={config.cooldown_minutes}
              onChange={e => setConfig({ ...config, cooldown_minutes: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!config.retries_enabled}
              onChange={e => setConfig({ ...config, retries_enabled: e.target.checked })} />
            Retries enabled globally
          </label>
          <button onClick={() => saveConfig(config)} className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded font-semibold text-sm">
            Save Config
          </button>
        </div>
      )}

      {/* Profiles */}
      {subtab === 'profiles' && (
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="font-bold text-gray-900 mr-auto">Profiles ({profiles.length})</h3>
            {['all', 'active', 'expired', 'exhausted', 'failed'].map(s => (
              <button key={s} onClick={() => setProfileFilter(s)}
                className={`px-3 py-1.5 rounded text-sm font-medium ${profileFilter === s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                data-testid={`vt-profile-filter-${s}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Student</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Plan</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Attempts</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Best Score</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{p.student_name || p.student_id}</div>
                      <div className="text-xs text-gray-500">{p.student_email}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{p.plan_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {p.attempts_used}/{p.attempts_assigned} used
                      <span className="text-gray-400"> · {p.attempts_remaining} left</span>
                    </td>
                    <td className="px-3 py-2 font-mono">{Math.round(p.best_score || 0)}%</td>
                    <td className="px-3 py-2">
                      {p.is_verified ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-semibold">Verified</span>
                      ) : p.status === 'exhausted' ? (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">Exhausted</span>
                      ) : p.status === 'expired' ? (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-semibold">Expired</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-semibold">Pending</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <button onClick={() => addAttempts(p.student_id)} className="px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">+ Attempts</button>
                        {!p.is_verified && <button onClick={() => manualApprove(p.student_id)} className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Approve</button>}
                        {p.is_verified && <button onClick={() => revoke(p.student_id)} className="px-2 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100">Revoke</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {profiles.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No profiles in this filter</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};



// ==================== LLM Health Tab ====================

const LLMHealthTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/api/admin/llm-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load LLM health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }
  if (!data) return null;

  const statusTone =
    data.status === 'ok'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : data.status === 'degraded'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-red-50 text-red-700 border-red-200';
  const statusLabel = data.status === 'ok' ? 'Healthy' : data.status === 'degraded' ? 'Degraded' : 'Down';
  const StatusIcon = data.status === 'ok' ? CheckCircle : data.status === 'degraded' ? AlertTriangle : XCircle;

  const counts = data.counts || {};
  const byKind = counts.by_kind || {};
  const endpoints = counts.endpoints_hit || [];
  const total = (counts.successes || 0) + (counts.failures || 0);
  const successRate = total > 0 ? Math.round(((counts.successes || 0) / total) * 100) : null;

  const kindColor = (k) => ({
    balance: 'bg-red-100 text-red-700',
    auth: 'bg-red-100 text-red-700',
    rate_limit: 'bg-amber-100 text-amber-700',
    other: 'bg-gray-100 text-gray-700',
  }[k] || 'bg-gray-100 text-gray-700');

  const ago = (iso) => {
    if (!iso) return '—';
    const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
  };

  return (
    <div className="space-y-6" data-testid="llm-health-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${statusTone}`}>
            <StatusIcon size={16} />
            {statusLabel}
          </div>
          <p className="text-sm text-gray-500">
            Last {Math.round((data.window_sec || 300) / 60)}-minute window
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="llm-health-auto-refresh"
            />
            Auto-refresh (15s)
          </label>
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="px-3 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="llm-health-refresh-btn"
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      {data.hint && (
        <div className={`p-4 rounded-lg border ${statusTone}`}>
          <p className="text-sm font-medium">{data.hint}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Successful calls" value={counts.successes || 0} tone="emerald" />
        <StatCard label="Failed calls" value={counts.failures || 0} tone={counts.failures ? 'red' : 'gray'} />
        <StatCard label="Success rate" value={successRate != null ? `${successRate}%` : '—'} tone={successRate == null ? 'gray' : successRate >= 95 ? 'emerald' : successRate >= 80 ? 'amber' : 'red'} />
        <StatCard label="Endpoints hit" value={endpoints.length} tone="gray" />
      </div>

      {Object.keys(byKind).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Failure breakdown</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byKind).map(([kind, n]) => (
              <span key={kind} className={`px-2.5 py-1 rounded-md text-xs font-medium ${kindColor(kind)}`}>
                {kind.replace('_', ' ')}: {n}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Last success</p>
          <p className="text-sm font-semibold text-gray-900">{ago(data.last_success_at)}</p>
          <p className="text-[11px] text-gray-500 mt-1">{data.last_success_at || '—'}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Last failure</p>
          <p className="text-sm font-semibold text-gray-900">{ago(data.last_error_at)}</p>
          {data.last_error_kind && (
            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${kindColor(data.last_error_kind)}`}>
              {data.last_error_kind.replace('_', ' ')}
            </span>
          )}
          {data.last_error_endpoint && (
            <p className="text-[11px] text-gray-500 mt-1">on {data.last_error_endpoint}</p>
          )}
          {data.last_error_message && (
            <p className="text-[11px] text-gray-600 mt-2 bg-gray-50 p-2 rounded border border-gray-100 break-words">
              {data.last_error_message}
            </p>
          )}
        </div>
      </div>

      {(data.recent_errors || []).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">Recent errors</h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {data.recent_errors.map((e, idx) => (
              <div key={idx} className="px-5 py-3 flex items-start gap-3">
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${kindColor(e.kind)}`}>
                  {e.kind}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-700">
                    {e.endpoint} · {ago(e.at)}
                  </p>
                  <p className="text-[11px] text-gray-500 break-words mt-1 font-mono">{e.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {total === 0 && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-8 text-center text-sm text-gray-500">
          No LLM activity in the last {Math.round((data.window_sec || 300) / 60)} minutes.
          Voice interviews and AI chats will show up here as they happen.
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, tone = 'gray' }) => {
  const toneMap = {
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    gray: 'text-gray-900',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${toneMap[tone] || toneMap.gray}`}>{value}</p>
    </div>
  );
};
